import { Component } from "@theme/component";
import {
  ThemeEvents,
  VariantUpdateEvent,
  ZoomMediaSelectedEvent,
  SlideshowSelectEvent,
} from "@theme/events";

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest(".shopify-section, dialog");

    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
      { signal },
    );
    // ZoomMediaSelectedEvent is dispatched by zoom-dialog when it closes,
    // carrying the final slide index so the main gallery can sync (Zoom → Main).
    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      { signal },
    );
    // When main slideshow changes while zoom dialog is open, sync zoom too (Main → Zoom live sync)
    this.refs.slideshow?.addEventListener(
      SlideshowSelectEvent.eventName,
      this.#handleMainSlideshowSelect,
      { signal },
    );
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector("media-gallery");

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles ZoomMediaSelectedEvent dispatched by zoom-dialog on close.
   * Syncs the main gallery to match the final zoom position (Zoom → Main).
   * @param {ZoomMediaSelectedEvent} event
   */
  #handleZoomMediaSelected = (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Handles SlideshowSelectEvent from the main slideshow.
   * If zoom dialog is currently open, syncs its position to match (Main → Zoom live sync).
   * @param {import('@theme/events').SlideshowSelectEvent} event
   */
  #handleMainSlideshowSelect = (event) => {
    console.log(event);
    const { dialog } = this.refs.zoomDialogComponent?.refs ?? {};
    if (!dialog?.open) return;
    const zoomSlideshow = /** @type {any} */ (dialog.querySelector("slideshow-component"));
    zoomSlideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get("media-gallery")) {
  customElements.define("media-gallery", MediaGallery);
}
